import { FC, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
	canProceed,
	customisedSentence,
	disenrollmentSentence,
	PropagationForecast,
	requiresAcknowledgment,
	updateSentence,
} from "./acknowledgment";

/**
 * SIMS-9031 — the confirmation shown before a disenrollment-threshold change reaches participants
 * who are already enrolled.
 *
 * This modal IS the safeguard, not a courtesy. Decision D1 accepted that lowering a threshold
 * disenrolls participants for absence they had already accrued, and D2 made this confirmation the
 * control that makes that acceptable — which is why the disenrollment figure has to be typed back
 * rather than merely displayed. Its copy is a compliance artifact (CR-5); the wording lives in
 * ./acknowledgment.ts with tests so it cannot drift silently.
 */

/**
 * SIMS-9032 AC1 — one participant behind the forecast number.
 *
 * CR-4: participant-identifying, confined to this authorised screen. OQ-17 (whether the list may
 * be exported or printed) is unanswered, so this is deliberately view-only — no export, no copy.
 */
export interface ForecastDisenrollment {
	enrollmentId: string;
	beneficiaryId?: string;
	daysAbsent: number;
	currentSegment?: { start: number; end: number };
	currentThreshold: number;
	newThreshold: number;
}

export interface ThresholdPropagationModalProps {
	open: boolean;
	onCancel: () => void;
	/**
	 * SIMS-9030 AC3 — the administrator may include participants holding their own threshold in
	 * THIS change. Passed back rather than stored, because per D3 the decision must not outlive the
	 * group of people it was made about.
	 */
	onConfirm: (
		acknowledgedDisenrollments: number,
		includeCustomised: boolean,
	) => void;
	/** Re-previews with customised participants included, so the counts reflect the choice. */
	onIncludeCustomisedChange?: (include: boolean) => void;
	forecast: PropagationForecast;
	/** The threshold being saved, shown so the administrator can see what they are confirming. */
	previousValue: number;
	nextValue: number;
	/**
	 * WHICH threshold moved, in words (FR-8). "segment 2 (weeks 7-12)" and "the plan default" are
	 * different events for the same participant, so a bare pair of numbers is not enough to confirm
	 * against.
	 */
	fieldLabel?: string;
	/** SIMS-9032 — the participants behind the disenrollment figure, inspectable before confirming. */
	forecastDisenrolled?: ForecastDisenrollment[];
	/** SIMS-9035 AC5 — a change is already running for this plan, so this one will be refused. */
	propagationInProgress?: boolean;
	/** AC7 — the forecast moved since it was shown; the figures below are the recomputed ones. */
	forecastChanged?: boolean;
	isSubmitting?: boolean;
}

const ThresholdPropagationModal: FC<ThresholdPropagationModalProps> = ({
	open,
	onCancel,
	onConfirm,
	onIncludeCustomisedChange,
	forecast,
	forecastDisenrolled = [],
	previousValue,
	nextValue,
	fieldLabel = "the plan default",
	propagationInProgress = false,
	forecastChanged = false,
	isSubmitting = false,
}) => {
	const [typed, setTyped] = useState("");
	// SIMS-9030 AC4: request-scoped. Deliberately local state — a stored flag decided today against
	// these participants would still be set months later against a different group (D3).
	const [includeCustomised, setIncludeCustomised] = useState(false);
	// SIMS-9032 AC3 — reviewing the list and going back must not lose the decision, so this is a
	// disclosure within the confirmation rather than a separate screen.
	const [showList, setShowList] = useState(false);

	// AC7 / D2: the typed figure acknowledges ONE forecast. When the forecast is recomputed the old
	// acknowledgment is void — clear it, so the administrator reads and types the new number rather
	// than carrying a figure that happens to match. Without this, a recomputed count equal to what
	// was already typed leaves Apply enabled on a figure nobody read, which defeats the control D2
	// makes the whole compliance safeguard ("the disenrollment figure is typed back, not ticked").
	useEffect(() => {
		setTyped("");
	}, [forecast.forecastDisenrollments]);

	const needsAcknowledgment = requiresAcknowledgment(forecast);
	// AC7 — "acknowledged again" means typing the NEW figure, which `canProceed` already enforces by
	// comparing against the CURRENT count. `forecastChanged` deliberately does NOT gate this: it is
	// the explanation (the info alert above), not a second lock.
	//
	// It used to be a term here, and because nothing ever cleared it the modal became unconfirmable
	// the moment the forecast moved — so an include-customised change (SIMS-9030 AC3) could never be
	// applied at all. Unticking the box happened to reset the flag, which is what made the block look
	// intermittent rather than absolute.
	//
	// The stale figure still cannot proceed: it no longer equals the new count, and the useEffect
	// above clears the field anyway. The server independently recomputes and 409s on any mismatch
	// (treatmentplans.controller.ts), so this is a UX gate, not the authority.
	const allowed =
		canProceed(forecast, typed) && !propagationInProgress && !isSubmitting;
	const customised = customisedSentence(forecast, includeCustomised);

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle>
				Apply this threshold change to current participants?
			</DialogTitle>

			<DialogContent dividers>
				<Stack spacing={2}>
					<Typography variant="body2">
						The disenrollment threshold for <strong>{fieldLabel}</strong> is
						changing from <strong>{previousValue} days</strong> to{" "}
						<strong>{nextValue} days</strong>.
					</Typography>

					{/* SIMS-9035 AC1/AC2 — refused, and told why, before they try. */}
					{propagationInProgress && (
						<Alert severity="warning">
							A threshold change is already being applied to this plan. Wait for
							it to finish before starting another.
						</Alert>
					)}

					{/* AC7 — the acknowledgment they were about to give no longer describes what
					    would happen, so the figures were recomputed and must be acknowledged again. */}
					{forecastChanged && (
						<Alert severity="info">
							The forecast has changed since it was shown. Please review the
							current figures below and acknowledge again.
						</Alert>
					)}

					{/* FR-11 — the three counts. They span two populations by design (D5): more
					    participants are written than can be disenrolled, because paused and
					    non-weekly participants are updated but never checked. */}
					<Box>
						<Typography variant="body2" data-testid="propagation-update-count">
							{updateSentence(forecast)}
						</Typography>

						{customised && (
							<>
								<Typography
									variant="body2"
									sx={{ mt: 1 }}
									data-testid="propagation-customised-count"
								>
									{customised}
								</Typography>

								{/* SIMS-9030 AC3 — the administrator can include them in THIS change.
								    Off by default (AC1): a deliberate clinical exception is not
								    erased by a routine plan edit unless someone says so. */}
								<FormControlLabel
									sx={{ mt: 0.5 }}
									control={
										<Checkbox
											size="small"
											checked={includeCustomised}
											onChange={(e) => {
												setIncludeCustomised(e.target.checked);
												// The counts change when they are included, and the
												// acknowledged number must match what will happen,
												// so the forecast is recomputed rather than adjusted
												// in the page.
												onIncludeCustomisedChange?.(e.target.checked);
											}}
											disabled={propagationInProgress || isSubmitting}
											inputProps={{
												"aria-label":
													"Also update participants who hold their own threshold",
											}}
										/>
									}
									label="Also update participants who hold their own threshold"
								/>
							</>
						)}
					</Box>

					<Alert severity={needsAcknowledgment ? "warning" : "success"}>
						<span data-testid="propagation-disenrollment-sentence">
							{disenrollmentSentence(forecast)}
						</span>
					</Alert>

					{/* SIMS-9032 AC1/AC4 — offered only when there is actually someone to review. */}
					{needsAcknowledgment && forecastDisenrolled.length > 0 && (
						<Box>
							<Button
								size="small"
								onClick={() => setShowList((open) => !open)}
								data-testid="propagation-review-toggle"
							>
								{showList
									? "Hide these participants"
									: "Review these participants"}
							</Button>

							<Collapse in={showList}>
								<Table size="small" data-testid="propagation-forecast-list">
									<TableHead>
										<TableRow>
											<TableCell>Participant</TableCell>
											<TableCell>Days absent</TableCell>
											<TableCell>Current phase</TableCell>
											<TableCell>Current</TableCell>
											<TableCell>New</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{forecastDisenrolled.map((row) => (
											<TableRow key={row.enrollmentId}>
												<TableCell>
													{row.beneficiaryId ?? row.enrollmentId}
												</TableCell>
												<TableCell>{row.daysAbsent}</TableCell>
												<TableCell>
													{row.currentSegment
														? `weeks ${row.currentSegment.start}-${row.currentSegment.end}`
														: "—"}
												</TableCell>
												<TableCell>{row.currentThreshold}</TableCell>
												<TableCell>{row.newThreshold}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Collapse>
						</Box>
					)}

					{/* AC2 — typed back, not ticked. A checkbox can be clicked without reading;
					    reproducing the number requires having looked at it. */}
					{needsAcknowledgment && (
						<TextField
							label={`Type ${forecast.forecastDisenrollments} to confirm you have read this`}
							value={typed}
							onChange={(e) => setTyped(e.target.value)}
							disabled={propagationInProgress || isSubmitting}
							inputProps={{
								"aria-label": "Acknowledge the forecast disenrollment count",
								inputMode: "numeric",
							}}
							size="small"
							fullWidth
						/>
					)}
				</Stack>
			</DialogContent>

			<DialogActions>
				{/* AC6 — cancelling applies nothing and leaves the previous threshold in place. */}
				<Button onClick={onCancel} disabled={isSubmitting}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="primary"
					disabled={!allowed}
					onClick={() =>
						onConfirm(forecast.forecastDisenrollments, includeCustomised)
					}
				>
					{isSubmitting ? "Applying…" : "Apply change"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ThresholdPropagationModal;
